import { useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ArrowLeftIcon, BookOpenIcon } from "@heroicons/react/24/outline";
import RoleOnly from "../../../routes/roleOnly";
import { CompanyGuideSection } from "../../../components/dashboard/companyGuide/CompanyGuideSection";

const Section = ({ id, title, children }) => (
  <section id={id} className="scroll-mt-24 border-b border-slate-100 py-8 last:border-b-0">
    <h2 className="text-lg font-semibold text-slate-900">{title}</h2>
    <div className="mt-3 space-y-3 text-sm leading-relaxed text-slate-600">{children}</div>
  </section>
);

export const UserGuideHoursGettingStarted = () => {
  const { t } = useTranslation();
  const location = useLocation();

  useEffect(() => {
    document.title = `${t("user_guide.hours.title")} | Promang`;
  }, [t]);

  useEffect(() => {
    const raw = location.hash?.replace(/^#/, "");
    if (!raw) return;
    const el = document.getElementById(raw);
    if (el) {
      window.requestAnimationFrame(() => {
        el.scrollIntoView({ behavior: "smooth", block: "start" });
      });
    }
  }, [location.hash]);

  return (
    <div className="min-h-screen bg-slate-50">
      <div className="mx-auto max-w-3xl px-4 py-8 sm:px-6 lg:px-8">
        <Link
          to="/dashboard/guide"
          className="mb-6 inline-flex items-center gap-2 text-sm font-semibold text-slate-700 hover:text-slate-900"
        >
          <ArrowLeftIcon className="h-4 w-4" />
          {t("user_guide.hours.back_to_hub")}
        </Link>

        <div className="rounded-[2rem] border border-slate-200 bg-white px-6 py-8 shadow-sm sm:px-8">
          <div className="mb-2 inline-flex items-center gap-2 rounded-full bg-slate-100 px-3 py-1 text-xs font-semibold uppercase tracking-[0.18em] text-slate-600">
            <BookOpenIcon className="h-4 w-4" />
            {t("user_guide.hours.badge")}
          </div>
          <h1 className="text-2xl font-semibold tracking-tight text-slate-950">{t("user_guide.hours.title")}</h1>
          <p className="mt-3 text-sm text-slate-600">{t("user_guide.hours.purpose")}</p>

          <div className="mt-6">
            <RoleOnly role="admin">
              <CompanyGuideSection />
            </RoleOnly>
          </div>

          <Section id="registration" title={t("user_guide.hours.section.registration.title")}>
            <p>{t("user_guide.hours.section.registration.p1")}</p>
            <p>{t("user_guide.hours.section.registration.p2")}</p>
            <p>{t("user_guide.hours.section.registration.p3")}</p>
          </Section>

          <Section id="visibility" title={t("user_guide.hours.section.visibility.title")}>
            <ul className="list-inside list-disc space-y-2">
              <li>{t("user_guide.hours.section.visibility.li1")}</li>
              <li>{t("user_guide.hours.section.visibility.li2")}</li>
            </ul>
          </Section>

          <Section id="steps" title={t("user_guide.hours.section.steps.title")}>
            <div className="space-y-6">
              <div>
                <h3 className="text-sm font-semibold text-slate-800">{t("user_guide.hours.section.steps.s1.title")}</h3>
                <p className="mt-1">{t("user_guide.hours.section.steps.s1.where")}</p>
                <ul className="mt-2 list-inside list-disc space-y-1">
                  <li>{t("user_guide.hours.section.steps.s1.b1")}</li>
                  <li>{t("user_guide.hours.section.steps.s1.b2")}</li>
                </ul>
              </div>
              <div>
                <h3 className="text-sm font-semibold text-slate-800">{t("user_guide.hours.section.steps.s2.title")}</h3>
                <p className="mt-1">{t("user_guide.hours.section.steps.s2.where")}</p>
                <ul className="mt-2 list-inside list-disc space-y-1">
                  <li>{t("user_guide.hours.section.steps.s2.b1")}</li>
                  <li>{t("user_guide.hours.section.steps.s2.b2")}</li>
                  <li>{t("user_guide.hours.section.steps.s2.b3")}</li>
                </ul>
              </div>
              <div>
                <h3 className="text-sm font-semibold text-slate-800">{t("user_guide.hours.section.steps.s3.title")}</h3>
                <p className="mt-1">{t("user_guide.hours.section.steps.s3.where")}</p>
                <ul className="mt-2 list-inside list-disc space-y-1">
                  <li>{t("user_guide.hours.section.steps.s3.b1")}</li>
                  <li>{t("user_guide.hours.section.steps.s3.b2")}</li>
                </ul>
              </div>
              <div>
                <h3 className="text-sm font-semibold text-slate-800">{t("user_guide.hours.section.steps.s4.title")}</h3>
                <p className="mt-1">{t("user_guide.hours.section.steps.s4.where")}</p>
                <ul className="mt-2 list-inside list-disc space-y-1">
                  <li>{t("user_guide.hours.section.steps.s4.b1")}</li>
                  <li>{t("user_guide.hours.section.steps.s4.b2")}</li>
                </ul>
              </div>
              <div>
                <h3 className="text-sm font-semibold text-slate-800">{t("user_guide.hours.section.steps.s5.title")}</h3>
                <p className="mt-1">{t("user_guide.hours.section.steps.s5.where")}</p>
                <ul className="mt-2 list-inside list-disc space-y-1">
                  <li>{t("user_guide.hours.section.steps.s5.b1")}</li>
                  <li>{t("user_guide.hours.section.steps.s5.b2")}</li>
                </ul>
              </div>
            </div>
          </Section>

          <Section id="public-holidays" title={t("user_guide.hours.section.public_holidays.title")}>
            <p>{t("user_guide.hours.section.public_holidays.p1")}</p>
            <p>{t("user_guide.hours.section.public_holidays.p2")}</p>
            <ul className="list-inside list-disc space-y-2">
              <li>{t("user_guide.hours.section.public_holidays.li1")}</li>
              <li>{t("user_guide.hours.section.public_holidays.li2")}</li>
              <li>{t("user_guide.hours.section.public_holidays.li3")}</li>
              <li>{t("user_guide.hours.section.public_holidays.li4")}</li>
            </ul>
          </Section>

          <Section id="related" title={t("user_guide.hours.section.related.title")}>
            <ul className="list-inside list-disc space-y-2">
              <li>
                <Link className="font-medium text-slate-900 underline decoration-slate-300" to="/dashboard/hours">
                  {t("user_guide.hours.section.related.link_hours")}
                </Link>
              </li>
              <li>
                <Link
                  className="font-medium text-slate-900 underline decoration-slate-300"
                  to="/dashboard/hours/approval"
                >
                  {t("user_guide.hours.section.related.link_approval")}
                </Link>
              </li>
              <li>
                <Link
                  className="font-medium text-slate-900 underline decoration-slate-300"
                  to="/dashboard/absences/overview"
                >
                  {t("user_guide.hours.section.related.link_absence")}
                </Link>
              </li>
              <li>
                <Link
                  className="font-medium text-slate-900 underline decoration-slate-300"
                  to="/dashboard/hours/monthly-overview"
                >
                  {t("user_guide.hours.section.related.link_monthly")}
                </Link>
              </li>
              <li>
                <Link
                  className="font-medium text-slate-900 underline decoration-slate-300"
                  to="/dashboard/users/work_schedule"
                >
                  {t("user_guide.hours.section.related.link_work_schedule")}
                </Link>
              </li>
              <li>
                <Link
                  className="font-medium text-slate-900 underline decoration-slate-300"
                  to="/dashboard/hours/settings"
                >
                  {t("user_guide.hours.section.related.link_settings")}
                </Link>
              </li>
            </ul>
          </Section>

          <Section id="more-help" title={t("user_guide.hours.section.more_help.title")}>
            <p>{t("user_guide.hours.section.more_help.p1")}</p>
            <p>
              <Link
                to="/dashboard/resource-hub/faq"
                className="font-semibold text-slate-900 underline decoration-slate-300"
              >
                {t("user_guide.hours.section.more_help.faq_link")}
              </Link>
            </p>
          </Section>
        </div>
      </div>
    </div>
  );
};
